<script setup>

import {onMounted, reactive, ref, watch} from "vue";
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {ToastUtil} from "@/Utils/ToastUtil";
import {userStore} from "@/Stores/UserStore";

const props = defineProps({
  status: String,
  personId: Number
});
const personId = ref(props.personId);

const emit = defineEmits(['closed','newUser']);

const data = reactive({
  temporaryPasswordShow: false,
  loading: true,
  dialog: true,
  temporaryPassword: '',
  adManagedUser: false,
  roles: [],
  permissions: [],
  roleList: []
});

const close = () => {
  emit('closed');
  clear();
}

const save = async () => {
  if(data.roles.length === 0) {
    ToastUtil.errorMsg('Users must have at least one role selected');
    return;
  }

  if(!data.loading) {
    data.loading = true;

    await ApiWrapper.post('user-management', 'create-user-from-person', {
          personId: personId.value,
          temporaryPassword: data.temporaryPassword,
          roles: data.roles,
          permissions: data.permissions,
          adManagedUser: data.adManagedUser
        })
        .then((response) => {
          emit('newUser');
          close();
          ToastUtil.successMsg('User account created for this person');
        })
        .catch((e) => {
          ToastUtil.error(e);
        })
        .finally(() => {
          data.loading = false;
        });
  }
}

const clear = () => {
  data.temporaryPassword = '';
  data.adManagedUser = false;
  data.roles = [];
  data.permissions = [];
}

const loadRoles = async () => {
  await ApiWrapper.get('auth-roles', 'list-roles')
  .then((response) => {
    const isAdmin = userStore.userInfo?.isAdmin || false;
    data.roleList = response.roles.map((role) => {
      // If the user is not an admin and does not have a given role you shouldn't be able to assign/remove it
      if(!isAdmin && !userStore.userInfo?.roleIds.includes(role.id)) {
        role.props = { disabled: true};
      }
      // Add an * by default roles
      return {
        ...role,
        role: role.is_default ? `${role.role}` : role.role
      };
    });
  })
  .catch((error) => {
    ToastUtil.error(error);
  })
  .finally(()=>{
    data.loading = false;
  });
}

watch(
    () => props.personId,
    (newValue, oldValue) => {
      personId.value = newValue;
    }
);

onMounted(async ()=>{
  await loadRoles();
});

</script>

<template>
  <div class="text-center">
    <v-dialog
        v-if="props.status === 'open'"
        v-model="data.dialog"
        max-width="600"
        persistent
        @click:outside="close"
    >
      <v-card>
        <v-toolbar flat color="white">
          <v-toolbar-title>Create Account</v-toolbar-title>
          <v-progress-linear
              :active="data.loading"
              :indeterminate="data.loading"
              absolute
              bottom
              color="primary"
          >
          </v-progress-linear>
        </v-toolbar>

        <v-card-text>
            <v-row>
              <v-col cols="12" sm="4">
                <v-checkbox
                    class="mr-4"
                    label="Active Directory User"
                    v-model="data.adManagedUser">
                </v-checkbox>
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field
                    v-if="!data.adManagedUser"
                    v-model="data.temporaryPassword"
                    label="Temp Password"
                    :append-icon="data.temporaryPasswordShow ? 'mdi-eye' : 'mdi-eye-off'"
                    :type="data.temporaryPasswordShow ? 'text' : 'password'"
                    @click:append="data.temporaryPasswordShow = !data.temporaryPasswordShow"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="4">
                <v-select
                    v-model="data.roles"
                    :items="data.roleList"
                    label="Roles"
                    item-title="role"
                    item-value="role"
                    chips
                    multiple
                ></v-select>
              </v-col>
            </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
              text="Close"
              variant="plain"
              @click="close"
          ></v-btn>

          <v-btn
              color="primary"
              text="Create"
              variant="tonal"
              :disabled="data.loading"
              @click="save"
          ></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>

.container {
  padding: 5px;
}

[v-cloak] {
  display: none;
}

</style>